import { prisma } from "@/lib/prisma";

const MAX_RETRIES = 5;

/**
 * Normalize anything a scanner / admin might type into the canonical
 * `WIG-NNNNNN` form. Accepts "WIG-000123", "wig-123", "123", a full
 * scan URL ("https://rivkywigs.com/w/WIG-000123"), or garbage (→ null).
 */
export function normalizeWigBarcode(raw: string | null | undefined): string | null {
  if (!raw) return null;
  let s = raw.trim();
  // Full URL from a phone-camera scan → keep the last path segment.
  const urlMatch = s.match(/\/w\/([^/?#]+)/i);
  if (urlMatch) s = urlMatch[1];
  s = s.toUpperCase().replace(/\s+/g, "");
  const m = s.match(/^(?:WIG-?)?(\d{1,9})$/);
  if (!m) return null;
  return `WIG-${m[1].padStart(6, "0")}`;
}

export type WigLabelState =
  | { state: "registered"; barcodeId: string; assetId: string }
  | { state: "reserved"; barcodeId: string; reservationId: string; notes: string | null; orderedAt: Date }
  | { state: "unknown"; barcodeId: string };

/**
 * Where does a label code stand? Used by the admin scan page, the
 * intake form's per-wig label field, and the public /w/ page so each
 * can tell "already a wig" from "blank label waiting to be sewn in"
 * from "not one of ours".
 */
export async function lookupWigLabel(code: string): Promise<WigLabelState | null> {
  const barcodeId = normalizeWigBarcode(code);
  if (!barcodeId) return null;
  const [asset, reservation] = await Promise.all([
    prisma.wigAsset.findUnique({ where: { barcodeId }, select: { id: true } }),
    prisma.wigBarcodeReservation.findUnique({
      where: { barcodeId },
      select: { id: true, notes: true, orderedAt: true, consumedAt: true },
    }),
  ]);
  if (asset) return { state: "registered", barcodeId, assetId: asset.id };
  if (reservation && !reservation.consumedAt) {
    return {
      state: "reserved",
      barcodeId,
      reservationId: reservation.id,
      notes: reservation.notes,
      orderedAt: reservation.orderedAt,
    };
  }
  return { state: "unknown", barcodeId };
}

/** Thrown when a pre-printed label can't be attached. `code` is safe to show admins. */
export class WigLabelClaimError extends Error {
  code: "invalid" | "unknown" | "already_registered";
  constructor(code: WigLabelClaimError["code"], message: string) {
    super(message);
    this.name = "WigLabelClaimError";
    this.code = code;
  }
}

async function claimReservedBarcode(
  rawCode: string,
  data: {
    ownerId: string;
    orderItemId?: string | null;
    productId?: string | null;
    name: string;
    description?: string | null;
    photoUrl?: string | null;
    source?: string;
  }
) {
  const barcodeId = normalizeWigBarcode(rawCode);
  if (!barcodeId) {
    throw new WigLabelClaimError("invalid", `"${rawCode}" isn't a wig label code. Expected WIG-000123.`);
  }
  const reservation = await prisma.wigBarcodeReservation.findUnique({
    where: { barcodeId },
    select: { id: true, consumedAt: true },
  });
  const existing = await prisma.wigAsset.findUnique({
    where: { barcodeId },
    select: { id: true, name: true },
  });
  if (existing) {
    throw new WigLabelClaimError(
      "already_registered",
      `${barcodeId} is already attached to "${existing.name}". Scan it at /admin/scan to open that wig instead.`
    );
  }
  if (!reservation) {
    throw new WigLabelClaimError(
      "unknown",
      `${barcodeId} isn't one of our pre-printed labels. Reserve blank labels at /admin/settings/wig-labels first, or leave the code empty to auto-assign.`
    );
  }

  try {
    return await prisma.$transaction(async (tx) => {
      const asset = await tx.wigAsset.create({
        data: {
          barcodeId,
          ownerId: data.ownerId,
          orderItemId: data.orderItemId || null,
          productId: data.productId || null,
          name: data.name,
          description: data.description || null,
          photoUrl: data.photoUrl || null,
          source: data.source || "rivky",
        },
        include: {
          owner: { select: { id: true, name: true, email: true, phone: true } },
        },
      });
      // Guarded: if two admins race on the same physical label, the
      // second one's asset create already failed on the unique
      // barcodeId; this update is belt-and-braces.
      await tx.wigBarcodeReservation.update({
        where: { id: reservation.id },
        data: { consumedAt: new Date(), consumedByWigAssetId: asset.id },
      });
      return asset;
    });
  } catch (error) {
    if ((error as { code?: string }).code === "P2002") {
      throw new WigLabelClaimError(
        "already_registered",
        `${barcodeId} was just attached to another wig. Scan it at /admin/scan to open that wig.`
      );
    }
    throw error;
  }
}

/**
 * Create a WigAsset with a unique auto-generated barcodeId.
 * Retries on unique constraint collision to handle concurrent requests.
 */
export async function createWigAsset(data: {
  ownerId: string;
  orderItemId?: string | null;
  productId?: string | null;
  name: string;
  description?: string | null;
  photoUrl?: string | null;
  // "rivky" (default — Rivky-sold inventory), "walk_in" (customer's own
  // wig tagged at intake), "consigned" (future supplier consignment).
  source?: string;
  // Pre-printed label code (WIG-NNNNNN) from a blank-label batch
  // reserved at /admin/settings/wig-labels. When given, the asset is
  // minted on THAT number and the matching WigBarcodeReservation is
  // stamped consumed in the same transaction. Throws
  // WigLabelClaimError if the code isn't a reserved, unused label.
  barcodeId?: string | null;
}) {
  if (data.barcodeId) {
    return claimReservedBarcode(data.barcodeId, data);
  }
  for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
    try {
      // Next barcode = 1 + max(WigAsset.barcodeId, WigBarcodeReservation.barcodeId).
      // Reserved barcodes (admin pre-printed labels sitting in the
      // studio waiting to be sewn in) are skipped so auto-assign never
      // picks a number that's physically printed on a label.
      const [lastAsset, lastReservation] = await Promise.all([
        prisma.wigAsset.findFirst({
          orderBy: { barcodeId: "desc" },
          select: { barcodeId: true },
        }),
        prisma.wigBarcodeReservation.findFirst({
          orderBy: { barcodeId: "desc" },
          select: { barcodeId: true },
        }),
      ]);

      const parseNum = (id: string | undefined): number => {
        if (!id) return 0;
        const match = id.match(/WIG-(\d+)/);
        return match ? parseInt(match[1], 10) : 0;
      };
      const nextNum = Math.max(parseNum(lastAsset?.barcodeId), parseNum(lastReservation?.barcodeId)) + 1 || 1;

      const barcodeId = `WIG-${String(nextNum).padStart(6, "0")}`;

      const asset = await prisma.wigAsset.create({
        data: {
          barcodeId,
          ownerId: data.ownerId,
          orderItemId: data.orderItemId || null,
          productId: data.productId || null,
          name: data.name,
          description: data.description || null,
          photoUrl: data.photoUrl || null,
          source: data.source || "rivky",
        },
        include: {
          owner: { select: { id: true, name: true, email: true, phone: true } },
        },
      });

      return asset;
    } catch (error) {
      // P2002 = unique constraint violation — retry with next number
      if ((error as { code?: string }).code === "P2002" && attempt < MAX_RETRIES - 1) {
        continue;
      }
      throw error;
    }
  }

  throw new Error("Failed to generate unique barcode after retries");
}

/**
 * Atomically claim the oldest available physical WigAsset for a freshly
 * paid order item. Called from /api/checkout/square right after stock
 * decrement so the customer's specific wig is earmarked the moment they
 * pay — admins still see + can override the binding on the order page,
 * and order-cancel unlinks via existing `orderItemId IN (...)` cleanup.
 *
 * FIFO by createdAt: oldest registered wig ships first.
 *
 * Race-safe: uses a guarded `updateMany WHERE orderItemId IS NULL` so a
 * concurrent checkout that gets the same findFirst row loses the second
 * write (count=0), and the loop retries with the next-oldest candidate.
 *
 * Returns the claimed asset's barcodeId, or null if no eligible wig
 * exists (legacy product without registered physicals — admin will
 * assign manually later via the existing "Assign physical wig" picker).
 */
export async function autoAssignWigAssetForOrderItem(
  orderItemId: string,
  productId: string
): Promise<string | null> {
  const MAX_ATTEMPTS = 5;
  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
    const candidate = await prisma.wigAsset.findFirst({
      where: { productId, isActive: true, orderItemId: null },
      orderBy: { createdAt: "asc" },
      select: { id: true, barcodeId: true },
    });
    if (!candidate) return null;

    const res = await prisma.wigAsset.updateMany({
      where: { id: candidate.id, orderItemId: null, isActive: true },
      data: { orderItemId },
    });
    if (res.count === 1) return candidate.barcodeId;
    // race lost — another checkout claimed this wig in the gap; retry
  }
  return null;
}

/**
 * Batch-create WigAssets for multiple order items.
 * Returns array of created assets. Skips items that already have a wigAsset.
 */
export async function createWigAssetsForOrderItems(
  items: Array<{ id: string; productName: string }>,
  ownerId: string
) {
  const created: Array<{ id: string; barcodeId: string; name: string }> = [];

  for (const item of items) {
    try {
      const asset = await createWigAsset({
        ownerId,
        orderItemId: item.id,
        name: item.productName,
      });
      created.push({ id: asset.id, barcodeId: asset.barcodeId, name: asset.name });
    } catch (error) {
      // P2002 on orderItemId means already registered — skip silently
      if ((error as { code?: string }).code === "P2002") {
        continue;
      }
      console.error(`Failed to create WigAsset for item ${item.id}:`, error);
    }
  }

  return created;
}
